import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SupportingLists } from "@/components/config/SupportingLists";
import { toast } from "sonner";
import { Pencil, Plus, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader } from "@/components/app/common";
import { DataTable } from "@/components/app/DataTable";
import { ImportDialog } from "@/components/app/ImportDialog";
import { useMe } from "@/components/app/MeContext";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";
import { Warehouses } from "@/components/logistics/Warehouses";

const FIELDS = [["name", "Transporter name *"], ["gstin", "GSTIN"], ["contact_name", "Contact person"], ["phone", "Phone"], ["email", "Email"], ["notes", "Notes"]];

function TransporterDialog({ value, onClose, onSaved }) {
  const [f, setF] = useState(Object.fromEntries(FIELDS.map(([k]) => [k, value?.[k] || ""])));
  const save = async () => {
    try {
      const r = value?.id ? await api.put(`/transporters/${value.id}`, f) : await api.post("/transporters", f);
      toast.success(`${r.data.code} saved`);
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="transporter-dialog">
        <DialogHeader><DialogTitle>{value?.id ? `Edit ${value.code}` : "New transporter"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">{FIELDS.map(([k, l]) => <div key={k} className={k === "name" || k === "notes" ? "col-span-2" : ""}><Label>{l}</Label>
          <Input value={f[k]} onChange={(e) => setF({ ...f, [k]: k === "gstin" ? e.target.value.toUpperCase() : e.target.value })} data-testid={`transporter-${k}-input`} /></div>)}</div>
        <p className="text-xs text-slate-500">Same name or same GSTIN as an existing transporter is blocked, so each transporter exists once.</p>
        <DialogFooter><Button onClick={save} data-testid="transporter-save-btn">Save transporter</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Transporters() {
  const { can } = useMe();
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const [imp, setImp] = useState(false);
  const { data, reload } = useFetch(`/transporters?q=${encodeURIComponent(q)}`);
  const toggle = async (t) => { try { await api.post(`/transporters/${t.id}/status`, { active: !t.active }); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const cols = [
    { key: "code", label: "Code", mono: true }, { key: "name", label: "Transporter" }, { key: "gstin", label: "GSTIN", mono: true },
    { key: "contact_name", label: "Contact" }, { key: "phone", label: "Phone" },
    { key: "active", label: "Active", exp: (t) => (t.active ? "Yes" : "No"), render: (t) => <span onClick={(e) => e.stopPropagation()}><Switch checked={t.active} disabled={!can("write")} onCheckedChange={() => toggle(t)} data-testid={`transporter-active-${t.code}`} /></span> },
    { key: "edit", label: "", exp: false, render: (t) => can("write") && <Button size="sm" variant="ghost" onClick={() => setEdit(t)} data-testid={`transporter-edit-${t.code}`}><Pencil className="h-4 w-4" /></Button> },
  ];
  return (
    <>
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <Input className="sm:max-w-xs bg-white" placeholder="Search name, code, GSTIN…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="transporters-search" />
        {can("write") && <Button variant="outline" className="sm:ml-auto" onClick={() => setImp(true)} data-testid="transporter-import-btn"><FileSpreadsheet className="h-4 w-4 mr-1" />Import Excel</Button>}
        {can("write") && <Button onClick={() => setEdit({})} data-testid="new-transporter-btn"><Plus className="h-4 w-4 mr-1" />New transporter</Button>}
      </div>
      <ImportDialog entity="transporters" title="Import transporters from Excel" open={imp} onOpenChange={setImp} onDone={reload} />
      <DataTable testid="transporters-table" columns={cols} rows={data} exportName="Transporters" exportPerm="transporters.export" empty="No transporters yet — add the ones you use" />
      {edit && <TransporterDialog value={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />}
    </>
  );
}

export default function OtherMasters() {
  const { can } = useMe();
  const [sp, setSp] = useSearchParams();
  const TABS = [["warehouses", "Warehouse Master", Warehouses], ["transporters", "Transporters", Transporters], ["transport-lists", "Transport modes", () => <SupportingLists only="transport_mode" />], ["warehouse-types", "Warehouse types", () => <SupportingLists only="warehouse_type" />]];
  return (
    <div data-testid="other-masters-page">
      <PageHeader title="Warehouse & Transport Masters" subtitle="Legal consigner details (warehouse address and state GSTIN) and the transport masters used on dispatches, movements and Delivery Challans" />
      <Tabs value={sp.get("tab") || "warehouses"} onValueChange={(v) => setSp({ tab: v })} data-testid="logistics-masters-tabs">
        <TabsList className="mb-3 flex-wrap h-auto">{TABS.map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`logistics-masters-tab-${k}`}>{l}</TabsTrigger>)}</TabsList>
        {TABS.map(([k, , C]) => <TabsContent key={k} value={k}><C /></TabsContent>)}
      </Tabs>
      {can("admin.settings") && <p className="text-xs text-slate-500 mt-4" data-testid="other-masters-admin-note">Movement types, LM types, numbering and other supporting lists are in <Link className="text-sky-700 underline" to="/admin/config">Admin → Configuration masters</Link>.</p>}
    </div>
  );
}
